import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Timer,
  Assignment,
  CheckCircle,
  Login,
  PersonAdd,
  ArrowForward,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import quizAPI from '../../services/quizAPI';
import toast from 'react-hot-toast';

const QuizPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        const response = await quizAPI.getById(id);
        const data = response?.data || response;
        setQuiz(data);
      } catch (err) {
        console.error('Erreur lors du chargement du quiz:', err);
        setError(err.response?.data?.message || 'Quiz introuvable');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuiz();
    }
  }, [id]);

  const handleStartQuiz = () => {
    if (!user) {
      // Sauvegarder l'URL actuelle pour revenir après connexion
      sessionStorage.setItem('redirectAfterLogin', `/quiz/${id}/preview`);
      navigate('/', { state: { openAuthTab: 1 } }); // Rediriger vers landing avec tab inscription
    } else {
      // Utilisateur connecté, démarrer le quiz
      navigate(`/quiz/${id}`);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem('redirectAfterLogin', `/quiz/${id}/preview`);
    navigate('/', { state: { openAuthTab: 0 } }); // Rediriger vers landing avec tab connexion
  };

  const handleSignup = () => {
    sessionStorage.setItem('redirectAfterLogin', `/quiz/${id}/preview`);
    navigate('/', { state: { openAuthTab: 1 } }); // Rediriger vers landing avec tab inscription
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={50} />
          <Typography variant="body2" color="text.secondary">
            Chargement du quiz...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !quiz) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          {error || 'Quiz introuvable'}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Retour à l'accueil
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
      {/* Hero Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                bgcolor: alpha('#fff', 0.2),
              }}
            >
              <QuizIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Chip
              label={quiz.status === 'published' ? 'Publié' : 'Brouillon'}
              color={quiz.status === 'published' ? 'success' : 'default'}
              sx={{ mb: 2 }}
            />
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {quiz.title}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
              {quiz.description}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Quiz Details */}
      <Container maxWidth="md" sx={{ mt: -4 }}>
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
          {/* Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {quiz.questions?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Questions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Timer sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {quiz.duration}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Minutes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {quiz.passingScore}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Note de passage
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Informations supplémentaires */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Informations
            </Typography>
            <Grid container spacing={2}>
              {quiz.instructions && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Instructions :</strong> {quiz.instructions}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Nombre de tentatives autorisées :</strong> {quiz.maxAttempts}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Afficher les réponses correctes :</strong> {quiz.showCorrectAnswers ? 'Oui' : 'Non'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Call to Action */}
          {!user ? (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Pour passer ce quiz, vous devez créer un compte ou vous connecter.
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonAdd />}
                  onClick={handleSignup}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Créer un compte gratuit
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Login />}
                  onClick={handleLogin}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Se connecter
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Vous êtes connecté en tant que <strong>{user.name}</strong>
              </Alert>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowForward />}
                onClick={handleStartQuiz}
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                Commencer le quiz
              </Button>
            </Box>
          )}
        </Paper>

        {/* Informations supplémentaires */}
        <Card sx={{ mt: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Qu'est-ce que Firdaws Quiz ?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Firdaws Quiz est une plateforme de quiz interactive conçue pour le coaching de groupe.
              Passez des quiz, suivez vos progrès et améliorez vos compétences !
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ✅ 100% Gratuit • ✅ Quiz illimités • ✅ Suivi des performances
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default QuizPreview;

